import React, { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from "../axiosinstance";
import { CiSearch } from "react-icons/ci";

const SearchBar = ({
  placeholder = 'Search...',
  value,
  apiEndpoint,
  resultKey = 'data',
  onResults,
  currentPage = 1, // Accept currentPage as prop
  debounceDelay = 400,
  fetchDefaultData,
  itemsPerPage = 50, // Accept itemsPerPage as prop
}) => {
  const [query, setQuery] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [selectedProject, setSelectedProject] = useState(() => {
    return JSON.parse(localStorage.getItem("selectedProject")) || [];
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem("selectedProject");
      const updated = stored ? JSON.parse(stored) : null;
      setSelectedProject(updated);
    };

    window.addEventListener("projectChanged", handleStorageChange);

    return () => {
      window.removeEventListener("projectChanged", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (typeof value === "string" && value !== query) {
      setQuery(value);
      setIsSearchActive(value.trim() !== "");
    }
  }, [value, query]);

  // Effect to handle pagination for search results
  useEffect(() => {
    if (apiEndpoint && isSearchActive && query.trim() !== '') {
      fetchSearchResults(query, currentPage);
    }
  }, [currentPage, isSearchActive, query, apiEndpoint]);

  const fetchSearchResults = async (searchText, page = 1) => {
    try {
      const res = await axiosInstance.get(apiEndpoint, {
        params: { 
          query: searchText,
          page,
          Items_per_page: itemsPerPage, // Make sure to include itemsPerPage
          project_selected: selectedProject,
        },
      });

      const allData = res.data;
      const data = resultKey ? res.data[resultKey] : res.data;

      // Send both results + total count + search state back to parent
      onResults({
        data: Array.isArray(data) ? data : [],
        totalRecords: allData.Total_records || allData.total_records || 0,
        isSearch: true,
        query: searchText,
      });
    } catch (error) {
      console.error("❌ Search error:", error);
      const status = error.response?.status;
      if (status === 403) {
        const userRole = localStorage.getItem("role_name");
        if (userRole?.toLowerCase() === "field technician") {
          console.warn("Field Technician has limited search access");
        }
      }
      onResults({ 
        data: [], 
        totalRecords: 0, 
        isSearch: true, 
        query: searchText 
      });
    }
  };

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    // Passive mode: only emit query changes; parent handles API/debounce logic.
    if (!apiEndpoint) {
      onResults?.({
        data: [],
        totalRecords: 0,
        isSearch: newQuery.trim() !== "",
        query: newQuery,
      });
      return;
    }

    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(() => {
      if (newQuery.trim() !== '') {
        setIsSearchActive(true);
        fetchSearchResults(newQuery, 1); // Always start from page 1 for new search
      } else {
        setIsSearchActive(false);
        // Clear search results and fetch default data
        onResults({ 
          data: [], 
          totalRecords: 0, 
          isSearch: false, 
          query: "" 
        });
        fetchDefaultData?.();
      }
    }, debounceDelay);

    setTypingTimeout(timeout);
  };

  const handleCloseSearch = () => {
    setQuery("");
    setIsSearchActive(false);
    
    // Clear search results
    onResults({ 
      data: [], 
      totalRecords: 0, 
      isSearch: false, 
      query: "" 
    });
    
    // Fetch default data to repopulate the table (active API mode only)
    if (apiEndpoint) fetchDefaultData?.();
  };

  return (
    <div style={{ marginBottom: '1rem', position: 'relative', width: '320px' }}>
      {/* Search Icon */}
      <CiSearch
        size={20}
        style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#888',
          pointerEvents: 'none',
        }}
      />

      {/* Input Field */}
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={handleInputChange}
        style={{
          padding: '10px 16px 10px 36px',
          borderRadius: '4px',
          border: '1px solid #ccc',
          width: '100%',
          fontSize: '14px',
        }}
      />

      {/* Clear Button (×) */}
      {query && (
        <span
          onClick={handleCloseSearch}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            fontSize: '16px',
            color: '#999',
          }}
        >
          &times;
        </span>
      )}
    </div>
  );
};

export default SearchBar;
